import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { getMatters, createNote } from '../api/client';
import Dialog from './Dialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface MatterOption {
  id: number;
  title: string;
  contact_name?: string | null;
}

export default function QuickNoteModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<MatterOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState<MatterOption | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [noteDate, setNoteDate] = useState('');
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && !selectedMatter) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, selectedMatter]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setResults([]);
      setSelectedMatter(null);
      setSubject('');
      setBody('');
      setNoteDate('');
    }
  }, [open]);

  const handleSearch = useCallback((q: string) => {
    setSearch(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await getMatters({ search: q.trim(), limit: 8, archive_filter: 'active' });
        setResults(res.items.map(m => ({
          id: m.id,
          title: m.title,
          contact_name: m.contact_name,
        })));
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, 300);
  }, []);

  const handleSave = async () => {
    if (!selectedMatter || (!subject.trim() && !body.trim())) return;
    setSaving(true);
    try {
      await createNote({
        matter_id: selectedMatter.id,
        subject: subject.trim() || null,
        body: body.trim() || null,
        created_at: noteDate ? new Date(noteDate + 'T12:00:00').toISOString() : null,
      });
      queryClient.invalidateQueries({ queryKey: ['notes', selectedMatter.id] });
      queryClient.invalidateQueries({ queryKey: ['activities', selectedMatter.id] });
      onClose();
    } catch {
      // stay open on error
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <Dialog
      onClose={onClose}
      ariaLabel="Quick Note"
      overlayStyle={{ alignItems: 'flex-start', paddingTop: '8vh' }}
    >
      <div
        style={{
          background: 'var(--card)',
          borderRadius: 'var(--radius-md)',
          width: '92%',
          maxWidth: 500,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.9rem', cursor: 'pointer', padding: 0, minHeight: 'auto' }}
          >
            Cancel
          </button>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>Quick Note</h3>
          <button
            onClick={handleSave}
            disabled={saving || !selectedMatter || (!subject.trim() && !body.trim())}
            style={{
              background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.9rem', fontWeight: 600,
              cursor: 'pointer', padding: 0, minHeight: 'auto',
              opacity: (!selectedMatter || (!subject.trim() && !body.trim())) ? 0.4 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div style={{ padding: '0.75rem 1rem', overflow: 'auto', flex: 1 }}>
          {/* Matter picker */}
          {!selectedMatter ? (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                border: '1px solid var(--border)', borderRadius: 8, padding: '0.5rem 0.75rem',
                marginBottom: '0.5rem',
              }}>
                <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search for a matter..."
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                  style={{
                    border: 'none', outline: 'none', fontSize: '0.9rem', flex: 1,
                    background: 'transparent', color: 'var(--text)',
                  }}
                />
              </div>
              {searching && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Searching...</p>}
              {results.map(m => (
                <div
                  key={m.id}
                  onClick={() => setSelectedMatter(m)}
                  style={{
                    padding: '0.6rem 0.5rem',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: '0.88rem', fontWeight: 500, color: 'var(--text)' }}>{m.title}</div>
                  {m.contact_name && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{m.contact_name} &middot; M-{m.id}</div>
                  )}
                </div>
              ))}
              {!searching && search.trim() && results.length === 0 && (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>No matters found</p>
              )}
            </div>
          ) : (
            <div>
              {/* Selected matter pill */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem',
                background: 'var(--primary-light)', borderRadius: 8, padding: '0.5rem 0.75rem',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>{selectedMatter.title}</div>
                  {selectedMatter.contact_name && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{selectedMatter.contact_name} &middot; M-{selectedMatter.id}</div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedMatter(null)}
                  style={{
                    background: 'none', border: 'none', fontSize: '1.1rem', color: 'var(--text-muted)',
                    cursor: 'pointer', padding: '0 0.2rem', lineHeight: 1, minHeight: 'auto',
                  }}
                >
                  &times;
                </button>
              </div>

              {/* Note form */}
              <input
                type="text"
                placeholder="Subject (optional)"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                autoFocus
                style={{
                  width: '100%', padding: '0.5rem 0.6rem', border: '1px solid var(--border)',
                  borderRadius: 6, fontSize: '0.9rem', marginBottom: '0.5rem', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Date</label>
                <input
                  type="date"
                  value={noteDate}
                  onChange={e => setNoteDate(e.target.value)}
                  style={{
                    padding: '0.4rem 0.5rem', border: '1px solid var(--border)',
                    borderRadius: 6, fontSize: '0.85rem', color: 'var(--text)',
                    background: 'var(--card)', flex: 1,
                  }}
                />
                {noteDate && (
                  <button
                    onClick={() => setNoteDate('')}
                    style={{
                      background: 'none', border: 'none', color: 'var(--text-muted)',
                      cursor: 'pointer', fontSize: '0.8rem', padding: 0, minHeight: 'auto',
                    }}
                  >
                    clear
                  </button>
                )}
              </div>
              <textarea
                placeholder="Note body..."
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={6}
                style={{
                  width: '100%', padding: '0.5rem 0.6rem', border: '1px solid var(--border)',
                  borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
